import React from "react";
import { useSelector } from "react-redux";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import AlternateEmailRoundedIcon from "@mui/icons-material/AlternateEmailRounded";
import FacebookIcon from "@mui/icons-material/Facebook";
import InstagramIcon from "@mui/icons-material/Instagram";
import LanguageRoundedIcon from "@mui/icons-material/LanguageRounded";
import LocalPhoneRoundedIcon from "@mui/icons-material/LocalPhoneRounded";
import PlaceRoundedIcon from "@mui/icons-material/PlaceRounded";
import PremiumHeader from "components/PremiumLanding/PremiumHeader";
import PremiumFooter from "components/PremiumLanding/PremiumFooter";
import "components/PremiumLanding/PremiumLanding.css";

const externalUrl = (value) => /^https?:\/\//i.test(value) ? value : `https://${String(value || "").replace(/^\/+/, "")}`;

export default function ContactUs() {
  const { t, i18n } = useTranslation();
  const auth = useSelector((state) => state.auth);
  const settings = useSelector((state) => state.settingsdata.settings) || {};
  const isRTL = i18n.dir() === "rtl";

  const socialLinks = [
    settings.FacebookHandle ? { label: t("follow_facebook"), value: settings.FacebookHandle, Icon: FacebookIcon } : null,
    settings.TwitterHandle ? { label: t("follow_twitter"), value: settings.TwitterHandle, text: "X" } : null,
    settings.InstagramHandle ? { label: t("follow_instagram"), value: settings.InstagramHandle, Icon: InstagramIcon } : null
  ].filter(Boolean);

  return (
    <div className="premium-landing premium-public-page" dir={isRTL ? "rtl" : "ltr"}>
      <Helmet>
        <title>{t("contact_us")} | Garahex</title>
        <meta name="description" content={`${settings.CompanyName || "Garahex"} ${settings.CompanyAddress || ""}`.trim()} />
      </Helmet>
      <a className="pl-skip-link" href="#pl-main-content">Skip to content</a>
      <PremiumHeader isLoggedIn={Boolean(auth && auth.profile && auth.profile.uid)} />

      <main id="pl-main-content" className="pl-public-main">
        <section className="pl-public-hero pl-public-hero--contact">
          <div className="pl-public-hero__glow" aria-hidden="true" />
          <div className="pl-container pl-public-hero__inner">
            <div className="pl-public-hero__copy pl-reveal is-visible">
              <p className="pl-eyebrow pl-eyebrow--light">CONTACT GARAHEX</p>
              <h1>{t("contact_us")}</h1>
              {settings.CompanyName ? <p>{settings.CompanyName}</p> : null}
            </div>
            <div className="pl-public-hero__panel" aria-hidden="true">
              <span><LocalPhoneRoundedIcon /></span>
              <strong>{t("email")}</strong>
              <strong>{t("phone")}</strong>
              <strong>Follow Us</strong>
            </div>
          </div>
        </section>

        <section className="pl-public-section">
          <div className="pl-container">
            <div className="pl-contact-grid">
              <article className="pl-contact-card pl-contact-card--wide">
                <span className="pl-contact-card__icon"><LanguageRoundedIcon /></span>
                <p className="pl-eyebrow">Company</p>
                <h2>{settings.CompanyName}</h2>
                <p>{settings.CompanyAddress}</p>
              </article>

              <article className="pl-contact-card">
                <span className="pl-contact-card__icon"><PlaceRoundedIcon /></span>
                <p className="pl-eyebrow">Address</p>
                <h2>{settings.CompanyAddress}</h2>
              </article>

              {settings.contact_email ? (
                <article className="pl-contact-card">
                  <span className="pl-contact-card__icon"><AlternateEmailRoundedIcon /></span>
                  <p className="pl-eyebrow">{t("email")}</p>
                  <h2><a href={`mailto:${settings.contact_email}`}>{settings.contact_email}</a></h2>
                </article>
              ) : null}

              {settings.CompanyPhone ? (
                <article className="pl-contact-card">
                  <span className="pl-contact-card__icon"><LocalPhoneRoundedIcon /></span>
                  <p className="pl-eyebrow">{t("phone")}</p>
                  <h2>{settings.CompanyPhone}</h2>
                </article>
              ) : null}

              <article className="pl-contact-card">
                <span className="pl-contact-card__icon"><FacebookIcon /></span>
                <p className="pl-eyebrow">Follow Us</p>
                {socialLinks.length ? (
                  <div className="pl-contact-socials">
                    {socialLinks.map(({ label, value, Icon, text }) => (
                      <a key={label} href={externalUrl(value)} target="_blank" rel="noreferrer" aria-label={label}>
                        {Icon ? <Icon aria-hidden="true" /> : <span aria-hidden="true">{text}</span>}
                      </a>
                    ))}
                  </div>
                ) : (
                  <p>Follow Us</p>
                )}
              </article>
            </div>
          </div>
        </section>
      </main>

      <PremiumFooter settings={settings} />
    </div>
  );
}
